import type {
  HostUser,
  LegalBlockSet,
  LegalTextKey,
  PaymentTestResult,
  StripeAccountLink,
  UsersSettings,
} from '@interfaces/settings.interface';
import type {
  BoAccountResponse,
  BoAccountUpdateRequest,
  BoBookingWorkflowResponse,
  BoOverviewResponse,
  BoPaymentsResponse,
  BoPaymentsUpdateRequest,
  BoWebsiteResponse,
  BoWebsiteUpdateRequest,
} from '@services/settings.adapter';

import { mockDb } from '../mock-db';

const host = mockDb.host;
const contacts = mockDb.contacts;

/*
 * The settings service reads every response through the adapters (toAccountSettings /
 * toWebsiteSettings / toPaymentsSettings / toSettingsSummary), so the mock state is kept in the
 * BACKEND (Bo*Response) shape — returning the front model here would be re-mapped and come back
 * blank. Writes accept the Bo*UpdateRequest the adapter emits and merge in place so edits round-trip.
 */

let account: BoAccountResponse = {
  hostId: host.id,
  name: `${host.name} S.L.`,
  externalCodeName: host.name,
  userName: host.externalCodeName,
  contactPersonId: host.contactPersonId,
  location: {
    address: host.location.address ?? 'Calle Principal 1',
    cityId: '1',
    countryId: 'ES',
    zipCode: host.location.zipCode ?? '04638',
    coordinates: {
      latitude: String(host.location.coordinates?.latitude ?? 37.1234),
      longitude: String(host.location.coordinates?.longitude ?? -1.8567),
    },
  },
  bookingCutOffDate: host.bookingCutOffDate,
  languages: { defaultLanguage: 'es', enabledLanguages: ['es', 'en'] },
};

type BoLegalRow = NonNullable<BoWebsiteResponse['legalTexts']>[number];
type BoFaqRow = NonNullable<BoWebsiteResponse['faqs']>[number];

const legalText = (id: string, locale: string, type: string, content: string): BoLegalRow => ({
  id,
  locale,
  type,
  content,
  enabled: true,
});

let website: BoWebsiteResponse = {
  enabled: true,
  domainTenant: {
    mainDomain: 'mojacardream.rentsody.es',
    domainAliases: ['www.mojacardream.com'],
    tenantSlug: host.externalCodeName,
  },
  branding: {
    title: 'Mojácar Dream',
    subtitle: 'Your home by the Mediterranean',
    brandName: host.name,
    logoUrl:
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAIAAACQd1PeAAAADElEQVR4nGOIiMgHAAIrASCC52CKAAAAAElFTkSuQmCC',
    faviconUrl:
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAIAAACQd1PeAAAADElEQVR4nGM4sSIbAAQXAdw/1C7nAAAAAElFTkSuQmCC',
    bannerUrl:
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAIAAACQd1PeAAAADElEQVR4nGNwy+sBAAI+AUEAjzRPAAAAAElFTkSuQmCC',
    primaryColor: '#58586f',
    secondaryColor: '#c8a86b',
  },
  seo: {
    seoTitle: 'Mojácar Dream · Holiday rentals',
    seoDescription: 'Book seafront holiday homes in Mojácar, Almería.',
    seoKeywords: ['mojacar', 'holiday rental', 'almeria', 'beach'],
    canonicalUrl: 'https://mojacardream.rentsody.es',
    robotsIndex: true,
    robotsFollow: true,
  },
  publicContact: {
    publicEmail: 'hola@mojacardream.es',
    publicPhone: '+34 600 000 000',
    publicWhatsapp: '+34 600 000 000',
    publicContactName: contacts[0]?.name ?? 'Mojácar Dream',
    publicAddressVisible: true,
  },
  publication: { status: 'PUBLISHED' },
  legalTexts: [
    legalText('legal-es-terms', 'es', 'TERMS', 'Términos y condiciones de ejemplo.'),
    legalText('legal-es-privacy', 'es', 'PRIVACY', 'Política de privacidad de ejemplo.'),
    legalText('legal-es-notice', 'es', 'LEGAL_NOTICE', 'Aviso legal de ejemplo.'),
    legalText('legal-es-cancel', 'es', 'CANCELLATION', 'Política de cancelación de ejemplo.'),
    legalText('legal-en-terms', 'en', 'TERMS', 'Sample terms and conditions.'),
    legalText('legal-en-privacy', 'en', 'PRIVACY', 'Sample privacy policy.'),
    legalText('legal-en-notice', 'en', 'LEGAL_NOTICE', 'Sample legal notice.'),
    legalText('legal-en-cancel', 'en', 'CANCELLATION', 'Sample cancellation policy.'),
  ],
  faqs: [
    {
      id: 'faq-1',
      locale: 'es',
      question: '¿A qué hora es el check-in?',
      answer: 'El check-in es a partir de las 16:00.',
      sortOrder: 1,
      enabled: true,
    },
    {
      id: 'faq-2',
      locale: 'es',
      question: '¿Se admiten mascotas?',
      answer: 'Depende del alojamiento; consulta la ficha.',
      sortOrder: 2,
      enabled: true,
    },
  ],
};

let payments: BoPaymentsResponse = {
  currency: host.currency,
  defaultGateway: 'STRIPE',
  paymentEnabled: true,
  refundManual: { enabled: true, traceable: true, status: 'ACTIVE' },
  redsys: {
    configured: true,
    enabled: true,
    merchantCodeMasked: '••••3999',
    terminalNumberMasked: '00•',
    terminalConfigured: true,
    secretConfigured: true,
  },
  stripeConnect: {
    configured: true,
    provider: 'STRIPE_CONNECT',
    environment: 'TEST',
    accountIdMasked: 'acct_••••••••4242',
    accountEmail: 'payments@mojacardream.es',
    accountCountry: 'ES',
    onboardingStatus: 'ACTIVE',
    paymentsEnabled: true,
    chargesEnabled: true,
    payoutsEnabled: true,
    detailsSubmitted: true,
    chargeFlow: 'DIRECT_CHARGE',
    checkoutMode: 'REDIRECT',
    applicationFeeType: 'PERCENTAGE',
    applicationFeePercentage: 2.5,
  },
};

interface LegalBlockSeed {
  id: string;
  es: string;
  en: string;
}

const LEGAL_BLOCK_LOCALES = ['es', 'en'] as const;

const PLACEHOLDER_ES = '<em>[Texto de ejemplo — pendiente de redacción legal.]</em>';
const PLACEHOLDER_EN = '<em>[Placeholder copy — pending legal review.]</em>';

const LEGAL_BLOCK_SEED: Record<LegalTextKey, LegalBlockSeed[]> = {
  termsText: [
    {
      id: 'terms-parties',
      es: `<h3>1. Partes y objeto</h3><p>El presente contrato regula la reserva de alojamiento entre el titular del sitio y el huésped. ${PLACEHOLDER_ES}</p>`,
      en: `<h3>1. Parties and purpose</h3><p>These terms govern the accommodation booking between the site owner and the guest. ${PLACEHOLDER_EN}</p>`,
    },
    {
      id: 'terms-law',
      es: `<h3>2. Ley aplicable y jurisdicción</h3><p>Este contrato se rige por la legislación española y por la normativa turística autonómica aplicable. ${PLACEHOLDER_ES}</p>`,
      en: `<h3>2. Governing law and jurisdiction</h3><p>These terms are governed by Spanish law and the applicable regional tourism regulations. ${PLACEHOLDER_EN}</p>`,
    },
  ],
  privacyText: [
    {
      id: 'privacy-controller',
      es: `<h3>1. Responsable del tratamiento</h3><p>Los datos facilitados serán tratados por el titular del sitio conforme al RGPD (UE) 2016/679. ${PLACEHOLDER_ES}</p>`,
      en: `<h3>1. Data controller</h3><p>The data you provide is processed by the site owner under GDPR (EU) 2016/679. ${PLACEHOLDER_EN}</p>`,
    },
    {
      id: 'privacy-rights',
      es: `<h3>2. Derechos de la persona interesada</h3><p>Puedes ejercer los derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad. ${PLACEHOLDER_ES}</p>`,
      en: `<h3>2. Your rights</h3><p>You may exercise your rights of access, rectification, erasure, objection, restriction and portability. ${PLACEHOLDER_EN}</p>`,
    },
  ],
  legalNoticeText: [
    {
      id: 'notice-owner',
      es: `<h3>1. Datos identificativos</h3><p>En cumplimiento de la Ley 34/2002 (LSSI-CE), se informa de los datos identificativos del titular del sitio. ${PLACEHOLDER_ES}</p>`,
      en: `<h3>1. Site owner details</h3><p>In compliance with Spanish Law 34/2002 (LSSI-CE), the site owner's identifying details are disclosed here. ${PLACEHOLDER_EN}</p>`,
    },
    {
      id: 'notice-terms-of-use',
      es: `<h3>2. Condiciones de uso</h3><p>El acceso al sitio atribuye la condición de usuario e implica la aceptación de estas condiciones. ${PLACEHOLDER_ES}</p>`,
      en: `<h3>2. Conditions of use</h3><p>Accessing this site grants user status and implies acceptance of these conditions. ${PLACEHOLDER_EN}</p>`,
    },
  ],
  cancellationPolicyText: [
    {
      id: 'cancellation-statutory',
      es: `<h3>1. Derecho de desistimiento</h3><p>Los contratos de alojamiento con fecha determinada están exceptuados del desistimiento (art. 103.l TRLGDCU). ${PLACEHOLDER_ES}</p>`,
      en: `<h3>1. Right of withdrawal</h3><p>Accommodation contracts with a specific date are exempt from the statutory withdrawal right (art. 103.l TRLGDCU). ${PLACEHOLDER_EN}</p>`,
    },
    {
      id: 'cancellation-refunds',
      es: `<h3>2. Reembolsos</h3><p>Los reembolsos aprobados se abonan por el mismo medio de pago empleado en la reserva. ${PLACEHOLDER_ES}</p>`,
      en: `<h3>2. Refunds</h3><p>Approved refunds are issued to the original payment method used for the booking. ${PLACEHOLDER_EN}</p>`,
    },
  ],
};

const legalBlockSets: LegalBlockSet[] = LEGAL_BLOCK_LOCALES.flatMap(locale =>
  (Object.keys(LEGAL_BLOCK_SEED) as LegalTextKey[]).map(legalType => ({
    legalType,
    locale,
    blocks: LEGAL_BLOCK_SEED[legalType].map((seed, index) => ({
      id: seed.id,
      order: index + 1,
      html: seed[locale],
    })),
  }))
);

let users: HostUser[] = [
  {
    userName: host.externalCodeName,
    email: contacts[0]?.email ?? null,
    role: 'OWNER',
    status: 'ACTIVE',
    lastAccess: '2026-07-22T18:42:00Z',
    invitedAt: null,
    acceptedAt: '2026-01-15T08:00:00Z',
  },
  {
    userName: `${host.externalCodeName}_admin`,
    email: contacts[1]?.email ?? null,
    role: 'ADMIN',
    status: 'ACTIVE',
    lastAccess: '2026-07-21T09:15:00Z',
    invitedAt: '2026-02-01T08:00:00Z',
    acceptedAt: '2026-02-02T09:00:00Z',
  },
  {
    userName: `${host.externalCodeName}_staff`,
    email: null,
    role: 'STAFF',
    status: 'ACTIVE',
    lastAccess: '2026-07-20T14:03:00Z',
    invitedAt: '2026-03-10T08:00:00Z',
    acceptedAt: '2026-03-11T10:30:00Z',
  },
  {
    userName: `${host.externalCodeName}_recepcion`,
    email: 'recepcion@rentsody-demo.es',
    role: 'READ_ONLY',
    status: 'INVITED',
    lastAccess: null,
    invitedAt: '2026-06-20T08:00:00Z',
    acceptedAt: null,
  },
  {
    userName: `${host.externalCodeName}_antiguo`,
    email: 'antiguo@rentsody-demo.es',
    role: 'STAFF',
    status: 'DISABLED',
    lastAccess: '2025-11-04T16:20:00Z',
    invitedAt: '2025-11-05T08:00:00Z',
    acceptedAt: '2025-11-06T12:00:00Z',
  },
];

export function getAccountSettingsMock(): BoAccountResponse {
  return account;
}

export function updateAccountSettingsMock(patch: BoAccountUpdateRequest): BoAccountResponse {
  account = {
    ...account,
    ...patch,
    location: patch.location ? { ...account.location, ...patch.location } : account.location,
    languages: patch.languages ? { ...account.languages, ...patch.languages } : account.languages,
  };
  return account;
}

export function getWebsiteSettingsMock(): BoWebsiteResponse {
  return website;
}

export function updateWebsiteSettingsMock(patch: BoWebsiteUpdateRequest): BoWebsiteResponse {
  website = {
    ...website,
    ...(patch.enabled !== undefined ? { enabled: patch.enabled } : {}),
    domainTenant: patch.domainTenant
      ? { ...website.domainTenant, ...patch.domainTenant }
      : website.domainTenant,
    branding: patch.branding ? { ...website.branding, ...patch.branding } : website.branding,
    seo: patch.seo ? { ...website.seo, ...patch.seo } : website.seo,
    publicContact: patch.publicContact
      ? { ...website.publicContact, ...patch.publicContact }
      : website.publicContact,
  };
  return website;
}

/** Persists one legal cell (type token + locale). Creates the row when it does not exist yet. */
export function saveLegalTextMock(
  type: string,
  locale: string,
  body: { content?: string; enabled?: boolean }
): BoLegalRow {
  const rows = website.legalTexts ?? [];
  const existing = rows.find(row => row.type === type && row.locale === locale);
  if (existing) {
    existing.content = body.content ?? existing.content;
    existing.enabled = body.enabled ?? existing.enabled;
    return existing;
  }
  const created: BoLegalRow = {
    id: `legal-${locale}-${type.toLowerCase()}`,
    locale,
    type,
    content: body.content ?? '',
    enabled: body.enabled ?? true,
  };
  website.legalTexts = [...rows, created];
  return created;
}

let faqCounter = 100;

export function createWebsiteFaqMock(body: Record<string, unknown>): BoFaqRow {
  const created: BoFaqRow = {
    id: `faq-${(faqCounter += 1)}`,
    locale: (body['locale'] as string) ?? 'es',
    question: (body['question'] as string) ?? '',
    answer: (body['answer'] as string) ?? '',
    sortOrder: (body['sortOrder'] as number) ?? (website.faqs?.length ?? 0) + 1,
    enabled: (body['enabled'] as boolean) ?? true,
  };
  website.faqs = [...(website.faqs ?? []), created];
  return created;
}

export function updateWebsiteFaqMock(id: string, body: Record<string, unknown>): BoFaqRow | null {
  const rows = website.faqs ?? [];
  const existing = rows.find(faq => faq.id === id);
  if (!existing) {
    return null;
  }
  Object.assign(existing, {
    question: (body['question'] as string) ?? existing.question,
    answer: (body['answer'] as string) ?? existing.answer,
    sortOrder: (body['sortOrder'] as number) ?? existing.sortOrder,
    enabled: (body['enabled'] as boolean) ?? existing.enabled,
    locale: (body['locale'] as string) ?? existing.locale,
  });
  return existing;
}

export function deleteWebsiteFaqMock(id: string): void {
  website.faqs = (website.faqs ?? []).filter(faq => faq.id !== id);
}

export function getWebsiteLegalBlocksMock(): LegalBlockSet[] {
  return legalBlockSets;
}

export function getPaymentsSettingsMock(): BoPaymentsResponse {
  return payments;
}

/** Simulates a "test integration" charge, succeeding for known PSP providers. */
export function testPaymentMock(provider: string): PaymentTestResult {
  const known = provider === 'STRIPE' || provider === 'REDSYS';
  return {
    provider,
    success: known,
    message: known
      ? `${provider} integration verified with a symbolic charge.`
      : `Unknown provider "${provider}". Configure it before testing.`,
  };
}

/**
 * Stands in for the Stripe AccountLink the backend has yet to mint: a throwaway one-time token
 * expiring in 5 minutes, so the panel exercises a real short-lived, single-use link.
 */
export function createStripeAccountLinkMock(): StripeAccountLink {
  const token = Math.random().toString(36).slice(2, 12);
  return {
    url: `https://connect.stripe.com/setup/e/${token}`,
    expiresAt: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
  };
}

/** Masks a stored credential leaving only its last four characters visible. */
function maskCredential(value: string): string {
  const trimmed = value.trim();
  return trimmed.length <= 4 ? '•'.repeat(trimmed.length) : `••••${trimmed.slice(-4)}`;
}

export function updatePaymentsSettingsMock(patch: BoPaymentsUpdateRequest): BoPaymentsResponse {
  const { redsys, ...rest } = patch;
  payments = {
    ...payments,
    ...rest,
    redsys: redsys
      ? {
          ...payments.redsys,
          configured:
            redsys.merchantCode || redsys.terminalNumber ? true : payments.redsys?.configured,
          secretConfigured: redsys.secret ? true : payments.redsys?.secretConfigured,
          merchantCodeMasked: redsys.merchantCode
            ? maskCredential(redsys.merchantCode)
            : payments.redsys?.merchantCodeMasked,
          terminalNumberMasked: redsys.terminalNumber
            ? maskCredential(redsys.terminalNumber)
            : payments.redsys?.terminalNumberMasked,
        }
      : payments.redsys,
  };
  return payments;
}

/** The flags live on the mock host, so GET /hosts returns what the PATCH wrote, as the backend does. */
export function getBookingWorkflowSettingsMock(): BoBookingWorkflowResponse {
  return {
    paymentTimeoutEnabled: host.paymentTimeoutEnabled,
    mirTimeoutEnabled: host.mirTimeoutEnabled,
  };
}

/** Merges the flags the PATCH carries; a null or missing flag keeps its value, as the backend does. */
export function updateBookingWorkflowSettingsMock(
  patch: BoBookingWorkflowResponse
): BoBookingWorkflowResponse {
  host.paymentTimeoutEnabled = patch.paymentTimeoutEnabled ?? host.paymentTimeoutEnabled;
  host.mirTimeoutEnabled = patch.mirTimeoutEnabled ?? host.mirTimeoutEnabled;
  return getBookingWorkflowSettingsMock();
}

export function getUsersSettingsMock(): UsersSettings {
  return { hostId: host.id, users };
}

/** Replaces the matching user by userName, or appends it when not present. */
export function upsertUserMock(user: HostUser): HostUser {
  const exists = users.some(current => current.userName === user.userName);
  users = exists
    ? users.map(current => (current.userName === user.userName ? user : current))
    : [...users, user];
  return user;
}

/** Removes the user with the matching userName. */
export function deleteUserMock(userName: string): void {
  users = users.filter(current => current.userName !== userName);
}

/* -------------------------------------------------------------------------- */
/*  Overview summary — backend shape, mapped by toSettingsSummary             */
/* -------------------------------------------------------------------------- */

/**
 * Builds the overview in the backend `BoOverviewResponse` shape (flat `sections[]` with
 * UPPERCASE keys). The summary only carries a section-level status — subsections are not
 * modelled — so the section tags track the current mock state and every panel still renders.
 */
export function getSettingsSummaryMock(): BoOverviewResponse {
  const accountReady = !!(
    account.name &&
    account.location?.address &&
    account.languages?.defaultLanguage
  );
  const websiteReady =
    !!website.enabled &&
    !!website.domainTenant?.mainDomain &&
    website.publication?.status === 'PUBLISHED';
  const paymentsReady = !!payments.paymentEnabled && payments.defaultGateway !== 'NONE';

  return {
    hostId: host.id,
    hostName: host.name,
    tenantSlug: host.externalCodeName,
    sections: [
      { key: 'ACCOUNT', status: accountReady ? 'ACTIVE' : 'INCOMPLETE', enabled: true },
      { key: 'WEBSITE', status: websiteReady ? 'ACTIVE' : 'READY', enabled: true },
      { key: 'PAYMENTS', status: paymentsReady ? 'ACTIVE' : 'READY', enabled: true },
      {
        key: 'USERS_ROLES',
        status: users.length ? 'ACTIVE' : 'NOT_CONFIGURED',
        enabled: users.length > 0,
      },
    ],
  };
}
